import {
	COLUMN_WIDTH,
	ROW_HEADER_HEIGHT,
	ROW_HEIGHT,
} from '@dealstack/frontend/app/pages/Valuations/components/table/constants';
import { useColumnFiltering } from '@dealstack/frontend/app/pages/Valuations/components/table/hooks/useColumnFiltering/useColumnFiltering';
import { useSubColumnToggle } from '@dealstack/frontend/app/pages/Valuations/components/table/hooks/useSubColumnToggle/useSubColumnToggle';
import { ClassifierOption } from '@dealstack/frontend/app/pages/Valuations/shared/entityClassiferBadgeConfig';
import { useByPersonValuationTableColumns } from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/ByPersonValuationTable/ByPersonValuationTable.utils';
import { toByPersonTableData } from '@dealstack/frontend/app/pages/Valuations/views/ValuationSummary/ByPersonValuationTable/toByPersonTableData';
import { Z_INDEX } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { flexRender } from '@dealstack/frontend/shared/tables/flexRender';
import {
	CurrencyType,
	PersonGroupSummary,
	ValuationSummaryGroupByPersonRes,
} from '@dealstack/shared/lib/generated';
import {
	getCoreRowModel,
	getExpandedRowModel,
	getFilteredRowModel,
	useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { chain } from 'lodash';
import { CSSProperties, useMemo, useRef, useState } from 'react';
import { SetNonNullable } from 'type-fest';
import { NewByPersonValuationRow } from './ByPersonValuationTable.utils.types';

export function ByPersonValuationTable({
	initialData,
	showFilterInputs: externalShowFilterInputs,
	onShowFilterInputsChange,
	customContainerStyles,
	classifierOptions,
	currency,
}: {
	initialData: NewByPersonValuationRow[];
	showFilterInputs?: boolean;
	onShowFilterInputsChange?: (show: boolean) => void;
	customContainerStyles?: CSSProperties;
	classifierOptions: ClassifierOption[];
	currency?: CurrencyType;
}) {
	const tableData = initialData;

	const tableContainerRef = useRef<HTMLDivElement>(null);

	const { toggleSubColumns, hasVisibleSubColumns, isLastSubColumn } =
		useSubColumnToggle<NewByPersonValuationRow>();

	const [columnVisibility, setColumnVisibility] = useState({
		securitiesHeld: false,
		receivables: false,
		cashOnBalanceSheet: false,
		securitiesIssued: false,
		payables: false,
	});

	const { columnFilters, setColumnFilters, showFilterInputs, updateColumnFilter } =
		useColumnFiltering({
			data: tableData,
			showFilterInputs: externalShowFilterInputs,
			onShowFilterInputsChange,
		});

	const columns = useByPersonValuationTableColumns({
		showFilterInputs,
		columnFilters,
		classifierOptions,
		updateColumnFilter,
		toggleSubColumns,
		hasVisibleSubColumns,
		isLastSubColumn,
		currency,
	});

	const table = useReactTable<NewByPersonValuationRow>({
		data: tableData,
		columns,
		state: {
			columnVisibility,
			columnFilters,
		},
		getSubRows: (row) => row.subRows,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onColumnVisibilityChange: setColumnVisibility,
		onColumnFiltersChange: setColumnFilters,
		columnResizeMode: 'onChange',
		defaultColumn: {
			minSize: COLUMN_WIDTH,
		},
	});

	const { rows } = table.getRowModel();
	const rowVirtualizer = useVirtualizer({
		count: rows.length,
		getScrollElement: () => tableContainerRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 5,
	});
	const virtualRows = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();

	return (
		<div
			ref={tableContainerRef}
			style={{
				overflow: 'auto',
				margin: 0,
				padding: 0,
				...customContainerStyles,
			}}
		>
			<table
				style={{
					display: 'grid',
					height: 'fit-content',
					width: '100%',
					gridTemplateRows: 'auto 1fr',
					border: 'none',
					borderCollapse: 'collapse',
					borderSpacing: 0,
				}}
			>
				<thead
					style={{
						display: 'grid',
						position: 'sticky',
						top: 0,
						zIndex: Z_INDEX.STICKY_TABLE_HEADER,
					}}
				>
					{table.getHeaderGroups().map((headerGroup) => (
						<tr
							key={headerGroup.id}
							style={{
								display: 'flex',
								minHeight: `${ROW_HEADER_HEIGHT}px`,
								boxSizing: 'border-box',
							}}
						>
							{headerGroup.headers.map((header) =>
								header.isPlaceholder ? null : (
									<th
										key={header.id}
										style={{
											display: 'flex',
											minWidth: COLUMN_WIDTH,
											flex: '1 1 0px',
										}}
									>
										{flexRender(
											header.column.columnDef.header,
											header.getContext()
										)}
									</th>
								)
							)}
						</tr>
					))}
				</thead>

				<tbody
					style={{
						display: 'grid',
						height: `${totalSize}px`,
						position: 'relative',
					}}
				>
					{virtualRows.map((virtualRow) => {
						const row = rows[virtualRow.index];
						if (!row) {
							return null;
						}

						return (
							<tr
								key={row.id}
								data-index={virtualRow.index}
								data-testindex={virtualRow.index}
								ref={(node) => rowVirtualizer.measureElement(node)}
								style={{
									display: 'flex',
									position: 'absolute',
									transform: `translateY(${virtualRow.start}px)`,
									width: '100%',
									minHeight: `${ROW_HEIGHT}px`,
									maxHeight: `${ROW_HEIGHT}px`,
								}}
							>
								{row.getVisibleCells().map((cell, cellIndex) => {
									return (
										<td
											key={cell.id}
											data-testindex={cellIndex}
											style={{
												display: 'flex',
												minWidth: COLUMN_WIDTH,
												flex: '1 1 0px',
											}}
										>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext()
											)}
										</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

export function ByPersonValuationTableWithData({
	showFilterInputs,
	onShowFilterInputsChange,
	customContainerStyles,
	summaryByPersonData,
}: {
	showFilterInputs?: boolean;
	onShowFilterInputsChange?: (show: boolean) => void;
	customContainerStyles?: CSSProperties;
	summaryByPersonData?: ValuationSummaryGroupByPersonRes;
} = {}) {
	const classifierOptions = useMemo(() => {
		if (!summaryByPersonData?.persons) {
			return [];
		}

		const hasValidIdAndName = (
			group: PersonGroupSummary
		): group is SetNonNullable<PersonGroupSummary, 'id' | 'name'> => {
			return !!(group.id && group.name);
		};

		const classifierOptions = chain(summaryByPersonData.persons)
			.flatMap((person) => person.groups)
			.filter(hasValidIdAndName)
			.uniqBy('id')
			.map((group) => ({
				label: group.name,
				value: group.id,
			}))
			.value();

		return classifierOptions;
	}, [summaryByPersonData]);

	const tableData = useMemo(() => {
		if (summaryByPersonData) {
			return toByPersonTableData(summaryByPersonData);
		}
		return [];
	}, [summaryByPersonData]);

	return (
		<ByPersonValuationTable
			initialData={tableData}
			showFilterInputs={showFilterInputs}
			onShowFilterInputsChange={onShowFilterInputsChange}
			customContainerStyles={customContainerStyles}
			classifierOptions={classifierOptions}
			currency={summaryByPersonData?.currency}
		/>
	);
}
